import { Flex, Tag, Typography } from 'antd';
import {
  IconChevronRight,
  IconShield,
  IconShieldLock,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import type { RoleCardRole } from '../../types';
import {
  isRolePermissionsEditable,
  resolveSystemRoleDescription,
  resolveSystemRoleLabel,
} from '../../utils';

const { Text, Paragraph } = Typography;

type SystemRoleCardProps = {
  role: RoleCardRole;
  userRoleName: string;
  showUsersCount: boolean;
  onView: (role: RoleCardRole) => void;
};

export function SystemRoleCard({
  role,
  userRoleName,
  showUsersCount,
  onView,
}: SystemRoleCardProps) {
  const { t } = useTranslation('authorization');
  const label = resolveSystemRoleLabel(role.name, t);
  const description = resolveSystemRoleDescription(role.name, t);
  const isReadOnlySuperAdmin = role.name === SystemRoleName.SUPER_ADMIN;
  const isEditable = isRolePermissionsEditable(role, {
    roleName: userRoleName,
  });
  const Icon = isReadOnlySuperAdmin ? IconShieldLock : IconShield;

  return (
    <button
      type="button"
      className="authorization-system-role-card"
      onClick={() => onView(role)}
      aria-label={t('roleCard.viewRole', { name: label })}
    >
      <Flex vertical className="authorization-system-role-card-inner" gap={12}>
        <Flex justify="space-between" align="flex-start" gap={12}>
          <span className="authorization-system-role-card-icon" aria-hidden>
            <Icon size={22} stroke={1.75} />
          </span>
          <Tag className="authorization-system-role-card-badge">
            {t('rolesTable.systemRole')}
          </Tag>
        </Flex>

        <Flex vertical gap={6} className="authorization-system-role-card-body">
          <Text className="authorization-system-role-card-title">{label}</Text>
          {description && (
            <Paragraph className="authorization-system-role-card-description">
              {description}
            </Paragraph>
          )}
        </Flex>

        <Flex
          vertical
          gap={10}
          className="authorization-system-role-card-footer"
        >
          <Flex gap={6} wrap className="authorization-system-role-card-tags">
            {role.permissionCount !== undefined && (
              <Tag className="authorization-system-role-card-meta-tag">
                {t('roleCard.permissionCount', { count: role.permissionCount })}
              </Tag>
            )}
            {isReadOnlySuperAdmin && (
              <Tag className="authorization-system-role-card-meta-tag authorization-system-role-card-meta-tag--muted">
                {t('roleCard.readOnly')}
              </Tag>
            )}
            {isEditable && (
              <Tag className="authorization-system-role-card-meta-tag authorization-system-role-card-meta-tag--editable">
                {t('roleCard.editable')}
              </Tag>
            )}
            {showUsersCount && (
              <Tag className="authorization-system-role-card-meta-tag">
                {t('roleCard.usersCount', { count: role.usersCount })}
              </Tag>
            )}
          </Flex>

          <Flex
            align="center"
            gap={4}
            className="authorization-system-role-card-action"
          >
            <Text>{t('roleCard.viewDetails')}</Text>
            <IconChevronRight size={16} stroke={2} />
          </Flex>
        </Flex>
      </Flex>
    </button>
  );
}
